import { Component, Input, OnInit, Output, EventEmitter, OnDestroy, ViewChild, TemplateRef, AfterViewInit } from '@angular/core';

import { BehaviorSubject, Subscription, take } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AbstractBaseStyleFieldComponent } from '../../../../Common/PageBuilder/components/BaseStyleField/abstract-base-style-field.component';
import { AbstractPageBuilderService } from '../../../../Common/PageBuilder/services/abstract-page-builder.service';
import { BriefingBlockTypeEnum } from '../../../Briefings/enums/briefing-block-type.enum';
import { BriefingDateStyleFieldModel } from '../../../Briefings/models/briefing-date-style-field.model';
import { MonitoringDateStyleModel } from '../../../Shared/models/monitoring-date-style-model';
import { briefingsStyleDropdownOptions } from '../../../Briefings/models/briefings-style-dropdown-options.model';
import { BriefingStyleStateEnum } from '../../../../Common/MonitorShared/enum/briefing-style-state.enum';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { DropdownFieldModel } from '../../../../Common/PageBuilder/models/dropdown-field.model';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { ManageStylesDialogComponent } from '../../../../Common/Styles/components/ManageStylesDialog/manage-styles-dialog.component';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { SaveTwigTemplateDialogComponent } from '../../../../Common/Dialogs/components/SaveTwigTemplateDialog/save-twig-template-dialog.component';
import { SaveTwigTemplateDialogModel } from '../../../../Common/Dialogs/models/save-twig-template-dialog.model';
import { Style } from '../../../../Common/Styles/models/style.model';
import { StylesService } from '../../../../Common/Styles/services/styles.service';
import { StyleTypesApiEnum } from '../../../../api/styles/enum/style-types-api.enum';
import { Template } from '../../../../Common/Styles/models/template.model';
import { TextStyleDefaultsModel } from '../../../../Common/PageBuilder/models/text-style-defaults.model';
import { TextStyleFieldModel } from '../../../../Common/PageBuilder/models/text-style-field.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { dateFormats } from '../../../../Core/Users/data/date-formats';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { BlockDisplayTypeEnum } from '../../../../Common/PageBuilder/enums/block-display-type.enum';
import { BriefingDeliveryMethodEnum } from '../../../Briefings/enums/briefing-delivery-method.enum';
import { UserPreferencesService } from '@Account/UserPreferences/services/user-preferences.service';
import { AdvancedColorPickerFieldModel, backgroundColorConfig } from '@Common/PageBuilder/models/advanced-color-picker-field.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';

@Component({
    selector: 'agpr-briefing-date-style-field',
    templateUrl: './briefing-date-style-field.component.html',
    styleUrls: ['./briefing-date-style-field.component.scss'],
    standalone: false
})
export class BriefingDateStyleFieldComponent extends AbstractBaseStyleFieldComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() declare value: MonitoringDateStyleModel;
    @Input() declare field: BriefingDateStyleFieldModel;
    @Input() deliveryMethod: BriefingDeliveryMethodEnum;

    @Output() changes: EventEmitter<MonitoringDateStyleModel> = new EventEmitter<MonitoringDateStyleModel>();

    @ViewChild('dateBackgroundColorPicker') public backgroundColorPickerRef: TemplateRef<any>;

    styleStateField: DropdownFieldModel;
    textStyleField: TextStyleFieldModel;
    backgroundColorField: AdvancedColorPickerFieldModel;
    dateFormatField: DropdownFieldModel;
    languageFormatField: DropdownFieldModel;

    contentBlockStyle: string;
    defaultTimezone: string = null;

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly styleTypes = StyleTypesApiEnum;


    protected saveTwigTemplateDialogModel: SaveTwigTemplateDialogModel = {
        inputs: {
            type: StyleTypesApiEnum.DATE,
            inputStyle: null
        },
        outputs: {
            dialogClose: (response) => {
                if (response) {
                    this.setStyle(response, 'displayStyleState', true);
                } else {
                    if (this.setTemplate) {
                        this.setTemplate = false;
                        delete this.styleModel.template;
                    }
                }
            }
        }
    };

    private defaultStyleStateOptions: DropdownOptionModel[] = briefingsStyleDropdownOptions;
    private subscriptions: Subscription[] = [];
    private styles: Style[];

    constructor(
        protected coreDialogService: CoreDialogService,
        private stylesService: StylesService,
        public briefingsService: AbstractPageBuilderService,
        private localizationService: LocalizationService,
        private userPreferencesService: UserPreferencesService,
        private currentUserStateService: CurrentUserStateService,
    ) {
        super(coreDialogService);

        this.subscriptions.push(
            this.stylesService.all$.pipe(
                filter(x => !!x)
            ).subscribe(styles => {
                this.styles = styles;

                this.updateStyleOptions();
                this.updateStyleFromSavedStyle();
            }),
            this.userPreferencesService.getUILanguages().pipe(filter(x => !!x),take(1)).subscribe(languages => {
                const languageFormatOptions = languages.map(x =>
                    new DropdownOptionModel(this.localizationService.transform(`Language.${x.languageTag}`), x.languageTag)
                ).sort((a, b) => a.label.localeCompare(b.label));

                this.languageFormatField = new DropdownFieldModel(
                    null,
                    'locale',
                    'DateStyleField.language',
                    null,
                    languageFormatOptions
                );
            })
        );
    }

    ngOnInit() {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x), take(1)).subscribe(preferences => {
                this.defaultTimezone = preferences.timeZone;
                if (this.block && !this.value.timezone && this.value.showTimeZone) {
                    this.value.timezone = this.defaultTimezone;
                    this.briefingsService.notifyBlockDisplayElementChanged(this.block);
                }
            })
        );

        if (this.block?.displayElement) {
            this.field.allowExpertMode = this.block?.displayElement?.displayType !== BlockDisplayTypeEnum.ONLINE;
        }

        if (this.field.changeDefaultStyleStateOptions) {
            this.defaultStyleStateOptions = this.field.changeDefaultStyleStateOptions(this);
        }
        this.styleStateField = new DropdownFieldModel(
            null,
            'displayStyleState',
            'PageBuilder.styles.contentBlockStyle',
            '',
            this.defaultStyleStateOptions
        );
        this.updateStyleOptions();

        this.textStyleField = new TextStyleFieldModel(
            'font',
            false,
            new TextStyleDefaultsModel(
                '14px',
                '#ffffff',
                true
            ),
            undefined,
            undefined,
            false
        );


        this.backgroundColorField = AdvancedColorPickerFieldModel.create({
            ...backgroundColorConfig(true),
            tooltipPosition: 'right'
        });


        // Date Format Dropdown
        const storedInNonPhpFormat = dateFormats.find(
            f => f.momentFormat === this.value.dateFormat
                || f.value.toLowerCase() === this.value.dateFormat?.toLowerCase()
        );
        if (storedInNonPhpFormat) {
            this.value.dateFormat = storedInNonPhpFormat.twigFormat;
        }
        const dateFormatOptions = dateFormats.map(format => new DropdownOptionModel(this.localizationService.transform(format.name), format.twigFormat));

        this.dateFormatField = new DropdownFieldModel(
            null,
            'dateFormat',
            'DateStyleField.dateFormat',
            null,
            dateFormatOptions
        );
    }

    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            this.textStyleField.customBackgroundColorTemplate = this.backgroundColorPickerRef;
        });
    }

    public onShowTimeZoneChange(checked: boolean): void {
        if (checked && !this.value.timezone) {
            this.value.timezone = this.defaultTimezone;
        }
        this.setStyle(checked, 'showTimeZone');
    }

    public onShowTimeChange(checked: boolean): void {
        if (checked && !this.value.timezone) {
            this.value.timezone = this.defaultTimezone;
        }
        this.setStyle(checked, 'showTime');
    }

    /**
     * Opens the save-twig-template-dialog after loading the data needed for it
     */
    public saveExpertTemplate(): void {
        if (!this.styleModel) {
            this.styleModel = new Style();
        }

        this.styleModel.style = this.value;
        this.styleModel.template = this.value.template;
        this.saveTwigTemplateDialogModel.inputs.defaultTemplate = this.briefingsService.getDefaultTemplateByBlockType(
            BriefingBlockTypeEnum.DATE,
            BriefingStyleStateEnum.BRIEFING_1
        );
        // Set the default template if there's not a template on this style already
        if (!this.styleModel.template) {
            this.styleModel.template = new Template();
            this.styleModel.template.template = this.saveTwigTemplateDialogModel.inputs.defaultTemplate;
        }

        this.saveTwigTemplateDialogModel.inputs.inputStyle = this.styleModel;
        const dialogKey = this.coreDialogService.open(SaveTwigTemplateDialogComponent, this.saveTwigTemplateDialogModel);
    }

    protected updateStyleFromSavedStyle() {
        if (!this.value || !this.styles || !Number(this.value.styleState)) {
            return;
        }
        const savedStyle: Style = this.styles.find(x => x.id === this.value.styleState as number);
        if (!savedStyle) {
            // the list doesn't contain the id we're looking for, can't load it
            return;
        }
        const dateStyleModel = new MonitoringDateStyleModel(savedStyle.style);
        dateStyleModel.styleState = this.value.styleState;
        dateStyleModel.displayStyleState = this.value.displayStyleState;
        dateStyleModel.template = savedStyle.template;

        // There are fields on the style object that aren't saved (the view in browser and show date settings), assigning in this order preserves those
        const apiModel = {
            ...this.value,
            ...dateStyleModel
        };

        // Because of how we have to preserve non-style information, if the saved style didn't have a font, remove it from the combined version so that the font
        // defaults can apply properly
        if (!(dateStyleModel as MonitoringDateStyleModel).font) {
            delete apiModel.font;
        }

        this.value = new MonitoringDateStyleModel(apiModel);
        this.changes.emit(this.value);
    }

    public manageStylesButtonClicked(): void {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        this.coreDialogService.open(ManageStylesDialogComponent, io);
    }

    private updateStyleOptions(): void {
        if (!this.styles || !this.value || !this.value.styleState) {
            return;
        }

        if (this.field.changeDefaultStyleStateOptions) {
            this.defaultStyleStateOptions = this.field.changeDefaultStyleStateOptions(this);
        }

        const styleDropdownOptions = this.styles.filter(
            style => style.type === StyleTypesApiEnum.DATE
        ).map(
            style => {
                return {
                    label: style.name,
                    value: style.id
                };
            }
        );

        this.styleStateField.options = [
            ...this.defaultStyleStateOptions,
            ...styleDropdownOptions
        ];
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
